import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators';
import store from '@/store';
import { VolumeHistory, Trade, Exchange } from '@/types/trade';
import { Trades } from '@/api/trades/Trades';

interface ExchangeTimeAndSales {
  bitfinex: Trade[];
  bitflyer: Trade[];
  bitmex: Trade[];
}
interface ExchangeVolumeHistory {
  bitfinex: VolumeHistory;
  bitflyer: VolumeHistory;
  bitmex: VolumeHistory;
}
interface BuySellVolumePerPeriod {
  BUY: number;
  SELL: number;
}
interface ExchangeVolumePerPeriod {
  bitfinex: BuySellVolumePerPeriod;
  bitflyer: BuySellVolumePerPeriod;
  bitmex: BuySellVolumePerPeriod;
}
export interface IExchangeTradesState {
  alertPeriod: number;
  exchangeFilter: Exchange[];
  exchangeVolumeHistory: ExchangeVolumeHistory;
  exchangeVolumePerPeriod: ExchangeVolumePerPeriod;
}

@Module({ dynamic: true, store, name: 'exchangeTrades', namespaced: true })
class ExchangeTrades extends VuexModule implements IExchangeTradesState {
  public alertPeriod = 10000;
  public exchangeFilter = [
    Exchange.Bitfinex,
    Exchange.Bitflyer,
    Exchange.Bitmex,
  ];
  public exchangeTimeAndSales: ExchangeTimeAndSales = {
    bitfinex: [],
    bitflyer: [],
    bitmex: [],
  };
  public exchangeVolumeHistory: ExchangeVolumeHistory = {
    bitfinex: {
      BUY: [],
      SELL: [],
    },
    bitflyer: {
      BUY: [],
      SELL: [],
    },
    bitmex: {
      BUY: [],
      SELL: [],
    },
  };
  public exchangeVolumePerPeriod: ExchangeVolumePerPeriod = {
    bitfinex: {
      BUY: 0,
      SELL: 0,
    },
    bitflyer: {
      BUY: 0,
      SELL: 0,
    },
    bitmex: {
      BUY: 0,
      SELL: 0,
    },
  };

  get marketTimeAndSales() {
    const filteredExchangeTimeAndSales = this.exchangeFilter.map((exchange) => {
      return this.exchangeTimeAndSales[exchange];
    });
    const flatExchangeTimeAndSales = ([] as any[])
      .concat(...filteredExchangeTimeAndSales)
      .sort((a, b) => {
        const compare = b.timestamp - a.timestamp;
        if (compare !== 0) {
          return compare;
        }
        return -1;
      }).slice(0, 18);
    return flatExchangeTimeAndSales;
  }

  @Mutation
  public UPDATE_TRADES({
    bitfinex,
    bitflyer,
    bitmex,
  }: {
    bitfinex: Trades;
    bitflyer: Trades;
    bitmex: Trades;
  }) {
    this.exchangeTimeAndSales.bitfinex = bitfinex.getTimeAndSales().slice(-100);
    this.exchangeTimeAndSales.bitflyer = bitflyer.getTimeAndSales().slice(-100);
    this.exchangeTimeAndSales.bitmex = bitmex.getTimeAndSales().slice(-100);

    const bitfinexVolumeHistory = bitfinex.getVolumeHistory(5000);
    const bitflyerVolumeHistory = bitflyer.getVolumeHistory(5000);
    const bitmexVolumeHistory = bitmex.getVolumeHistory(5000);

    const bitfinexVolumePerPeriod = bitfinex.getVolumePerPeriod(
      this.alertPeriod,
    );
    const bitflyerVolumePerPeriod = bitflyer.getVolumePerPeriod(
      this.alertPeriod,
    );
    const bitmexVolumePerPeriod = bitmex.getVolumePerPeriod(this.alertPeriod);

    this.exchangeVolumeHistory.bitfinex.BUY = bitfinexVolumeHistory.BUY;
    this.exchangeVolumeHistory.bitfinex.SELL = bitfinexVolumeHistory.SELL;
    this.exchangeVolumeHistory.bitflyer.BUY = bitflyerVolumeHistory.BUY;
    this.exchangeVolumeHistory.bitflyer.SELL = bitflyerVolumeHistory.SELL;
    this.exchangeVolumeHistory.bitmex.BUY = bitmexVolumeHistory.BUY;
    this.exchangeVolumeHistory.bitmex.SELL = bitmexVolumeHistory.SELL;
    this.exchangeVolumePerPeriod.bitfinex.BUY = bitfinexVolumePerPeriod.BUY;
    this.exchangeVolumePerPeriod.bitfinex.SELL = bitfinexVolumePerPeriod.SELL;
    this.exchangeVolumePerPeriod.bitflyer.BUY = bitflyerVolumePerPeriod.BUY;
    this.exchangeVolumePerPeriod.bitflyer.SELL = bitflyerVolumePerPeriod.SELL;
    this.exchangeVolumePerPeriod.bitmex.BUY = bitmexVolumePerPeriod.BUY;
    this.exchangeVolumePerPeriod.bitmex.SELL = bitmexVolumePerPeriod.SELL;
  }
  @Mutation
  public SET_FILTER(filter: Exchange[]) {
    this.exchangeFilter = filter;
  }

  @Action({ commit: 'SET_FILTER' })
  public setFilter(filter: Exchange[]) {
    return filter;
  }
}

export const ExchangeTradesModule = getModule(ExchangeTrades);
